/** Resolves what the manage header's inline title editor binds to, from the compiled schema. */
import { CompiledField, CompiledFieldInstance, CompiledHeader } from '../../core/runtime/compiled.form';

/**
 * What the header needs to mount `wb-field` as an editable title.
 *
 * `instance` is optional on purpose. `wb-field` falls back to bind-level state when no rendered
 * occurrence is passed, and the identifier bind frequently has none: a surface that renames through
 * the header alone declares the bind `systemHidden` so it never also appears in the rail or a tab.
 * The compiler drops systemHidden fields from `fieldInstances` (they render nowhere) but keeps them
 * in `fields` (they still carry metadata, validators, and required), which is exactly what the
 * header needs.
 */
export interface ManageHeaderIdentifierEditor {
  readonly bind: string;
  readonly metadata: CompiledField['fieldMetadata'];
  readonly instancePath?: string;
}

/**
 * Picks the compiled source the editable title binds to: the rendered occurrence when the bind is
 * also on screen somewhere, otherwise the bind's compiled field.
 *
 * Returns null when the header is not editable or the identifier bind is not declared as a field at
 * all — a header identifier is a model path, not a field, so a surface that never declares it has
 * no metadata to build an editor from.
 */
export function resolveManageHeaderIdentifierEditor(header: CompiledHeader | undefined, fields: readonly CompiledField[], fieldInstances: readonly CompiledFieldInstance[]): ManageHeaderIdentifierEditor | null {

  if (!header?.editable) return null;

  const instance = fieldInstances.find(item => item.bind === header.identifier);
  if (instance) return { bind: instance.bind, metadata: instance.fieldMetadata, instancePath: instance.path };

  const field = fields.find(item => item.bind === header.identifier);
  if (!field) return null;

  return { bind: field.bind, metadata: field.fieldMetadata };
}
